import { css, cssManager } from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { zIndexLayers } from '../../00zindex.js';

export const appuiAppbarStyles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        --appbar-height: var(--dees-control-height-lg);
        --appbar-font-size: var(--dees-font-caption1-size);
        --appbar-fg-strong: var(--dees-color-text-primary);
        --appbar-fg: var(--dees-color-text-secondary);
        --appbar-fg-faint: var(--dees-color-text-muted);
        --appbar-wash-strong: var(--dees-color-fill-secondary);
        --appbar-separator: var(--dees-color-border-subtle);
        --appbar-control-border: var(--dees-color-border-subtle);
        
        display: block;
        position: relative;
        width: 100%;
        height: var(--appbar-height);
        border-bottom: 1px solid var(--appbar-separator);
        background:
          linear-gradient(
            to bottom,
            color-mix(in srgb, var(--dees-color-bg-primary) 18%, transparent),
            transparent 72%
          ),
          var(--dees-material-thin-bg);
        -webkit-backdrop-filter: var(--dees-material-thin-filter);
        backdrop-filter: var(--dees-material-thin-filter);
        color: var(--appbar-fg);
        font-size: var(--appbar-font-size);
        font-weight: 400;
        line-height: 1;
        letter-spacing: var(--dees-font-caption1-tracking);
        display: grid;
        grid-template-columns: minmax(0, auto) minmax(96px, 1fr) auto;
        -webkit-app-region: drag;
        user-select: none;
      }

      /* While a top-level menu is open, lift the appbar above the submenu
         overlay's transparent dismiss layer so sibling menu items stay hoverable
         (menu-bar switch-on-hover). Still below the submenu overlay itself
         (contextMenu z-index), so the open menu paints on top. */
      :host([menu-open]) {
        z-index: ${zIndexLayers.overlay.dropdown};
      }

      @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
        :host {
          background: var(--dees-material-thin-bg-opaque);
        }
        .dropdown {
          background: var(--dees-material-regular-bg-opaque);
        }
      }

      @media (prefers-reduced-transparency: reduce) {
        :host {
          background: var(--dees-material-thin-bg-opaque);
          -webkit-backdrop-filter: none;
          backdrop-filter: none;
        }
      }

      .menus {
        display: flex;
        align-items: center;
        min-width: 0;
        height: 100%;
        gap: 2px;
        padding: 0 8px 0 0;
        cursor: default;
        overflow: hidden;
      }

      .menus dees-windowcontrols {
        align-self: stretch;
        flex: 0 0 auto;
      }

      .menuItem {
        position: relative;
        min-height: 26px;
        line-height: 1;
        padding: 0 10px;
        margin: 0;
        border: 1px solid transparent;
        border-radius: var(--dees-radius-md);
        -webkit-app-region: no-drag;
        transition:
          background var(--dees-transition-fast) var(--dees-ease-standard),
          border-color var(--dees-transition-fast) var(--dees-ease-standard),
          color var(--dees-transition-fast) var(--dees-ease-standard),
          box-shadow var(--dees-transition-fast) var(--dees-ease-standard);
        cursor: default;
        outline: none;
        display: flex;
        align-items: center;
        gap: 6px;
        color: var(--appbar-fg);
        font-weight: 500;
        white-space: nowrap;
      }

      /* Optional: Style for menu items with icons (not typically used for top-level items) */
      .menuItem dees-icon {
        font-size: 13px;
        opacity: 0.8;
      }

      .menuItem:hover {
        background: var(--dees-color-hover);
        color: var(--appbar-fg-strong);
      }

      .menuItem.active {
        background: var(--appbar-wash-strong);
        border-color: var(--appbar-control-border);
        box-shadow: inset 0 1px 0 color-mix(in srgb, var(--dees-color-bg-primary) 22%, transparent);
        color: var(--appbar-fg-strong);
      }

      .menuItem[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }

      .menuItem:focus-visible {
        box-shadow: 0 0 0 2px var(--dees-color-focus-ring);
      }


      /* Dropdown styles */
      .dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        min-width: 200px;
        /* Legacy inline dropdown path; appbar submenus normally use the context menu overlay. */
        background: var(--dees-material-regular-bg);
        -webkit-backdrop-filter: var(--dees-material-regular-filter);
        backdrop-filter: var(--dees-material-regular-filter);
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-xl);
        box-shadow: var(--dees-shadow-lg);
        margin-top: var(--dees-spacing-xs);
        z-index: 1000;
        opacity: 0;
        transform: scale(0.98) translateY(-6px);
        transform-origin: top left;
        transition:
          opacity var(--dees-transition-default) var(--dees-ease-standard),
          transform var(--dees-transition-default) var(--dees-ease-standard);
        pointer-events: none;
        overflow: hidden;
      }

      .dropdown.open {
        opacity: 1;
        transform: scale(1) translateY(0);
        pointer-events: auto;
      }

      .dropdown-item {
        min-height: 28px;
        padding: 0 var(--dees-spacing-md);
        cursor: default;
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        transition: background var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .dropdown-item:hover,
      .dropdown-item.focused {
        background: var(--dees-color-active);
      }

      .dropdown-divider {
        height: 1px;
        background: var(--dees-color-border-subtle);
        margin: 4px 0;
      }

      .dropdown-item[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }

      .dropdown-item .shortcut {
        margin-left: auto;
        opacity: 0.6;
        font-size: 11px;
      }

      /* Breadcrumbs */
      .breadcrumbs {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 0;
        height: 100%;
        padding: 0 16px;
        overflow: hidden;
        color: var(--appbar-fg-faint);
        white-space: nowrap;
      }

      .breadcrumb-item {
        display: inline-block;
        min-width: 0;
        max-width: 180px;
        overflow: hidden;
        text-overflow: ellipsis;
        color: var(--appbar-fg);
        cursor: default;
        transition: color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .breadcrumb-item:hover {
        color: var(--appbar-fg-strong);
      }

      .breadcrumb-item:last-child {
        color: var(--appbar-fg-strong);
        font-weight: 500;
      }

      .breadcrumb-separator {
        margin: 0 6px;
        color: var(--appbar-fg-faint);
      }

      /* Account section */
      .account {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        min-width: max-content;
        height: 100%;
        padding: 0 10px 0 8px;
        gap: 6px;
        overflow: hidden;
      }

      .search-icon {
        width: 28px;
        height: 28px;
        display: grid;
        place-items: center;
        border-radius: var(--dees-radius-full);
        cursor: default;
        color: var(--appbar-fg);
        font-size: 15px;
        transition:
          background var(--dees-transition-fast) var(--dees-ease-standard),
          color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .search-icon:hover {
        background: var(--dees-color-hover);
        color: var(--appbar-fg-strong);
      }

      .user-info {
        display: flex;
        align-items: center;
        gap: 7px;
        height: 28px;
        cursor: default;
        padding: 0 9px 0 4px;
        border: 1px solid transparent;
        border-radius: var(--dees-radius-full);
        color: var(--appbar-fg);
        font-weight: 500;
        transition:
          background var(--dees-transition-fast) var(--dees-ease-standard),
          border-color var(--dees-transition-fast) var(--dees-ease-standard),
          color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .user-info:hover {
        background: var(--dees-color-hover);
        color: var(--appbar-fg-strong);
      }

      .user-avatar {
        position: relative;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: var(--appbar-wash-strong);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        font-weight: 600;
        color: var(--appbar-fg-strong);
        box-shadow: inset 0 0 0 1px var(--dees-color-border-subtle);
      }

      .user-avatar img {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
      }

      .user-status {
        position: absolute;
        bottom: -2px;
        right: -2px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        border: 2px solid var(--dees-material-thin-bg-opaque);
      }

      .user-status.online {
        background: var(--dees-color-accent-success);
      }

      .user-status.offline {
        background: var(--dees-color-text-muted);
      }

      .user-status.busy {
        background: var(--dees-color-accent-error);
      }

      .user-status.away {
        background: var(--dees-color-accent-warning);
      }

      /* Activity log toggle button */
      .activity-toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        height: 28px;
        min-width: 28px;
        padding: 0 8px;
        border-radius: var(--dees-radius-full);
        cursor: default;
        -webkit-app-region: no-drag;
        color: var(--appbar-fg);
        border: 1px solid transparent;
        transition:
          background var(--dees-transition-fast) var(--dees-ease-standard),
          border-color var(--dees-transition-fast) var(--dees-ease-standard),
          color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .activity-toggle:hover {
        background: var(--dees-color-hover);
        color: var(--appbar-fg-strong);
      }

      .activity-toggle.active {
        background: var(--appbar-wash-strong);
        color: var(--appbar-fg-strong);
        border-color: var(--appbar-control-border);
      }

      .activity-toggle dees-icon {
        font-size: 14px;
      }

      .activity-badge {
        position: relative;
        margin-left: 4px;
        min-width: 16px;
        height: 16px;
        padding: 0 4px;
        background: var(--dees-color-accent-primary);
        color: var(--dees-color-on-accent);
        font-size: 10px;
        font-weight: 600;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }

      @media (prefers-reduced-motion: reduce) {
        .menuItem,
        .dropdown,
        .dropdown-item,
        .breadcrumb-item,
        .search-icon,
        .user-info,
        .activity-toggle {
          transition: none;
        }
      }

      @media (max-width: 720px) {
        :host {
          grid-template-columns: minmax(0, 1fr) auto;
        }

        .breadcrumbs {
          display: none;
        }

        .menuItem {
          padding: 0 8px;
        }

        .account {
          padding-inline: 4px 6px;
        }

        .user-info span {
          display: none;
        }
      }
    `,
  ];
